import { useTranslation } from "react-i18next";

import { ScaleField } from "./ScaleField";

import type { AnswersDraft, SurveySection } from "@/features/respond/data/types";

interface SurveySectionViewProps {
  section: SurveySection;
  answers: AnswersDraft;
  onAnswer: (questionId: string, value: number | string) => void;
}

/**
 * SurveySectionView — A〜D など 1 section をまとめて描画する。
 *
 * 紙票同様 section 単位で 1 画面に流し、各設問を上から順に答える形にする。
 * 質問の type に応じて描画を分岐するが、現状は four-scale のみ。
 * 将来 single-choice / text を本物質問で使う場合に備えて switch だけ用意してある。
 */
export function SurveySectionView({ section, answers, onAnswer }: SurveySectionViewProps) {
  const { t } = useTranslation("respond");
  return (
    <section className="space-y-6">
      <header className="space-y-2">
        <h2 className="text-xl font-semibold">{section.title}</h2>
        {section.description && (
          <p className="text-base leading-relaxed text-muted-foreground">{section.description}</p>
        )}
        {section.scale && (
          <p className="pt-2 text-base text-muted-foreground">
            {t("form.scalePrefix")}
            {section.scale.map((s) => `${s.value}. ${s.label}`).join(" / ")}
          </p>
        )}
      </header>

      <div className="space-y-5">
        {section.questions.map((q) => {
          const current = answers[q.id];

          if (q.type === "four-scale") {
            if (!section.scale) return null;
            return (
              <ScaleField
                key={q.id}
                name={q.id}
                label={q.label}
                scale={section.scale}
                value={typeof current === "number" ? current : undefined}
                onChange={(v) => onAnswer(q.id, v)}
                required={q.required}
              />
            );
          }

          if (q.type === "single-choice") {
            return (
              <fieldset
                key={q.id}
                className="space-y-3 border-t border-border pt-5 first:border-t-0 first:pt-0"
              >
                <legend className="text-lg leading-relaxed">
                  {q.label}
                  {q.required && <span className="ml-1 text-destructive">*</span>}
                </legend>
                <div className="flex flex-wrap gap-x-6 gap-y-3">
                  {q.choices.map((c) => (
                    <label
                      key={c.value}
                      className="flex cursor-pointer items-center gap-2 text-base"
                    >
                      <input
                        type="radio"
                        name={q.id}
                        value={c.value}
                        checked={current === c.value}
                        onChange={() => onAnswer(q.id, c.value)}
                        className="size-5 accent-primary"
                      />
                      <span>{c.label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            );
          }

          if (q.type === "text") {
            return (
              <div
                key={q.id}
                className="space-y-3 border-t border-border pt-5 first:border-t-0 first:pt-0"
              >
                <label htmlFor={q.id} className="block text-lg leading-relaxed">
                  {q.label}
                  {q.required && <span className="ml-1 text-destructive">*</span>}
                </label>
                <textarea
                  id={q.id}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-base shadow-xs focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 outline-none"
                  rows={3}
                  placeholder={q.placeholder}
                  value={typeof current === "string" ? current : ""}
                  onChange={(e) => onAnswer(q.id, e.target.value)}
                />
              </div>
            );
          }

          return null;
        })}
      </div>
    </section>
  );
}
